import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChartRange, PriceHistory } from '../../types/finance'
import { getPriceHistory } from '../../lib/finance-api'

// Hand-rolled SVG price chart (no chart deps, matching charts.tsx): range
// tabs, area fill, and a hover crosshair — vertical line + dot + a tooltip
// with the date and closing price under the cursor.

const RANGES: ChartRange[] = ['1D', '5D', '1M', '6M', '1Y', '5Y', 'Max']

const W = 660
const H = 220
const PAD_T = 14
const PAD_B = 6

function isIntraday(interval: string): boolean {
  return /m|h/.test(interval) && !/mo/.test(interval)
}

function fmtPointDate(iso: string, intraday: boolean): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return intraday
    ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function PriceChart({
  symbol,
  anchorPrice,
  anchorPrevClose,
}: {
  symbol: string
  anchorPrice?: number
  anchorPrevClose?: number | null
}) {
  const [range, setRange] = useState<ChartRange>('1D')
  const [history, setHistory] = useState<PriceHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const [hoverFrac, setHoverFrac] = useState(0)
  const svgWrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setHoverIdx(null)
    getPriceHistory(symbol, range, anchorPrice, anchorPrevClose)
      .then((h) => {
        if (!cancelled) setHistory(h)
      })
      .catch(() => {
        if (!cancelled) setHistory(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [symbol, range, anchorPrice, anchorPrevClose])

  const model = useMemo(() => {
    const points = history?.points ?? []
    if (points.length < 2) return null
    let lo = Infinity
    let hi = -Infinity
    for (const p of points) {
      if (p.close < lo) lo = p.close
      if (p.close > hi) hi = p.close
    }
    const prevClose = history!.range === '1D' ? history!.previousClose : null
    if (prevClose != null) {
      if (prevClose < lo) lo = prevClose
      if (prevClose > hi) hi = prevClose
    }
    if (lo === hi) {
      lo -= 1
      hi += 1
    }
    const pad = (hi - lo) * 0.04
    lo -= pad
    hi += pad
    const x = (i: number) => (i / (points.length - 1)) * W
    const y = (v: number) => PAD_T + (1 - (v - lo) / (hi - lo)) * (H - PAD_T - PAD_B)
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.close).toFixed(1)}`).join(' ')
    const areaPath = `${linePath} L${W},${H} L0,${H} Z`
    const baseline = prevClose ?? points[0].close
    const last = points[points.length - 1].close
    const change = last - baseline
    const changePct = baseline !== 0 ? (change / baseline) * 100 : 0
    return { points, x, y, linePath, areaPath, lo, hi, prevClose, change, changePct, up: change >= 0 }
  }, [history])

  function pointerAt(clientX: number) {
    if (!model || !svgWrapRef.current) return
    const rect = svgWrapRef.current.getBoundingClientRect()
    if (rect.width === 0) return
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    setHoverFrac(frac)
    setHoverIdx(Math.round(frac * (model.points.length - 1)))
  }

  const hover = model && hoverIdx != null ? model.points[hoverIdx] : null
  const intraday = history ? isIntraday(history.interval) : false
  const strokeCls = model?.up ? 'stroke-emerald-500' : 'stroke-rose-500'
  const fillCls = model?.up ? 'fill-emerald-500/10' : 'fill-rose-500/10'

  return (
    <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-slate-900 dark:text-white font-semibold text-sm">Price</h3>
          {model && (
            <span
              className={`text-xs font-medium ${
                model.up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {model.up ? '+' : ''}
              {model.change.toFixed(2)} ({model.up ? '+' : ''}
              {model.changePct.toFixed(2)}%) over {range === 'Max' ? 'max range' : range}
            </span>
          )}
          {history?.isMock && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200/70 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400">
              SAMPLE
            </span>
          )}
        </div>
        <div className="flex bg-slate-200/60 dark:bg-slate-700/60 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                range === r
                  ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white font-semibold shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-[180px] bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
      ) : !model ? (
        <p className="h-[180px] flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
          No price history available.
        </p>
      ) : (
        <div className="relative">
          {/* Hover tooltip: date + closing price */}
          {hover && (
            <div
              className="absolute -top-1 z-10 -translate-x-1/2 pointer-events-none whitespace-nowrap px-2 py-1 rounded-md bg-slate-900/90 dark:bg-slate-950/90 text-white text-[11px] shadow-lg"
              style={{ left: `${Math.min(88, Math.max(12, hoverFrac * 100))}%` }}
            >
              <span className="font-semibold">${hover.close.toFixed(2)}</span>
              <span className="text-slate-300 ml-1.5">{fmtPointDate(hover.t, intraday)}</span>
            </div>
          )}

          <div
            ref={svgWrapRef}
            className="cursor-crosshair touch-none"
            onMouseMove={(e) => pointerAt(e.clientX)}
            onMouseLeave={() => setHoverIdx(null)}
            onTouchStart={(e) => pointerAt(e.touches[0].clientX)}
            onTouchMove={(e) => pointerAt(e.touches[0].clientX)}
            onTouchEnd={() => setHoverIdx(null)}
          >
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`${symbol} ${range} price chart`}>
              <path d={model.areaPath} className={fillCls} />
              <path d={model.linePath} fill="none" strokeWidth={1.8} className={strokeCls} strokeLinejoin="round" />
              {/* Previous-close reference (1D) */}
              {model.prevClose != null && (
                <line
                  x1={0}
                  x2={W}
                  y1={model.y(model.prevClose)}
                  y2={model.y(model.prevClose)}
                  strokeDasharray="4 3"
                  strokeWidth={1}
                  className="stroke-slate-400 dark:stroke-slate-500"
                />
              )}
              {/* Crosshair */}
              {hover && hoverIdx != null && (
                <g>
                  <line
                    x1={model.x(hoverIdx)}
                    x2={model.x(hoverIdx)}
                    y1={PAD_T - 6}
                    y2={H}
                    strokeWidth={1}
                    className="stroke-slate-500 dark:stroke-slate-300"
                    strokeDasharray="3 2"
                  />
                  <circle
                    cx={model.x(hoverIdx)}
                    cy={model.y(hover.close)}
                    r={3.5}
                    className={`${model.up ? 'fill-emerald-500' : 'fill-rose-500'} stroke-white dark:stroke-slate-900`}
                    strokeWidth={1.5}
                  />
                </g>
              )}
            </svg>
          </div>

          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 mt-1">
            <span>{fmtPointDate(model.points[0].t, intraday)}</span>
            <span>
              L ${model.points.reduce((m, p) => Math.min(m, p.close), Infinity).toFixed(2)} · H $
              {model.points.reduce((m, p) => Math.max(m, p.close), -Infinity).toFixed(2)}
              {model.prevClose != null && <> · prev close ${model.prevClose.toFixed(2)}</>}
            </span>
            <span>{fmtPointDate(model.points[model.points.length - 1].t, intraday)}</span>
          </div>
        </div>
      )}
    </div>
  )
}
